import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { t, errorText, dateTime, timeOnly, localeTag } from '../i18n/index.ts';
import { api, newKey } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Avatar, Dialog, ErrorView, Icon, Loading, useToast } from '../components/ui.tsx';
import type { MessageView } from '../../server/domain/chat.ts';

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🃏', '💰', '💩'];
const MAX_VOICE_MS = 120_000;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const URL_RE = /(https?:\/\/[^\s<>"']+)/gi;

/** Plain text with web links made clickable (opened in a new tab, no referrer). */
function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((p, i) => {
        if (i % 2 === 0) return p;
        const trimmed = p.replace(/[),.!?]+$/, '');
        return (
          <span key={i}>
            <a href={trimmed} target="_blank" rel="noopener noreferrer nofollow" className="msg-link">
              {trimmed}
            </a>
            {p.slice(trimmed.length)}
          </span>
        );
      })}
    </>
  );
}

function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  const closeBtn = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    // Focus moves into the viewer, stays there (one control) and returns when it closes.
    const previous = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key === 'Tab') {
        e.preventDefault();
        closeBtn.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, []);
  return (
    <motion.div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t('chat.image')}
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <button ref={closeBtn} type="button" className="round-btn ghost lightbox-close" aria-label={t('app.close')} onClick={onClose}>
        <Icon name="close" />
      </button>
      <motion.img src={url} alt="" initial={{ scale: 0.94 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()} />
    </motion.div>
  );
}

function ChatImage({ image, onOpen }: { image: NonNullable<MessageView['image']>; onOpen: () => void }) {
  return (
    <button type="button" className="bubble-image" onClick={onOpen} aria-label={t('chat.openImage')}>
      <img
        src={image.url}
        alt=""
        loading="lazy"
        width={image.width}
        height={image.height}
        style={{ aspectRatio: `${image.width || 4} / ${image.height || 3}` }}
      />
      {image.animated && <span className="gif-badge">GIF</span>}
    </button>
  );
}

const PROVIDER_NAME = { tiktok: 'TikTok', youtube: 'YouTube', instagram: 'Instagram' } as const;

function playerUrl(e: NonNullable<MessageView['embed']>): string | null {
  if (!e.videoId) return null;
  if (e.provider === 'tiktok') return `https://www.tiktok.com/player/v1/${e.videoId}?rel=0&description=1`;
  if (e.provider === 'instagram') return `https://www.instagram.com/${/\/reels?\//.test(e.url) ? 'reel' : 'p'}/${e.videoId}/embed/`;
  return `https://www.youtube-nocookie.com/embed/${e.videoId}?autoplay=1&rel=0`;
}

/**
 * Instagram's official embed always wraps the video in its own post frame (header, likes,
 * comment box). The frame's layout is fixed: a 54 px header, then the media in a 4:5 box, and a
 * reel's 9:16 video sits in the middle of that box. We show only the video part of the official
 * embed, so the reel fills the preview like TikTok's player; a link to the post stays below.
 */
function InstagramEmbed({ embed, src }: { embed: NonNullable<MessageView['embed']>; src: string }) {
  const reel = /\/reels?\//.test(embed.url);
  return (
    <div className="embed-card instagram">
      <div className={`ig-crop ${reel ? 'reel' : 'post'}`}>
        <iframe src={src} title={embed.title ?? 'Instagram'} loading="lazy" scrolling="no" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" />
      </div>
      <a className="embed-meta ig-link" href={embed.url} target="_blank" rel="noopener noreferrer">
        <span className="embed-title">{embed.author ?? 'Instagram'}</span>
        <span className="embed-author">{t('chat.openInstagram')}</span>
      </a>
    </div>
  );
}

/** Video link preview: thumbnail first; the provider's own player loads only when tapped. */
function EmbedCard({ embed }: { embed: NonNullable<MessageView['embed']> }) {
  const [playing, setPlaying] = useState(false);
  if (embed.status === 'failed') return null;
  const src = playerUrl(embed);
  const tall = embed.provider !== 'youtube';
  if (embed.provider === 'instagram' && src && embed.status === 'ready') return <InstagramEmbed embed={embed} src={src} />;
  if (playing && src) {
    return (
      <div className={`embed-player ${tall ? 'tall' : 'wide'} ${embed.provider}`}>
        <iframe
          src={src}
          title={embed.title ?? PROVIDER_NAME[embed.provider]}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }
  return <EmbedThumb embed={embed} src={src} tall={tall} onPlay={() => setPlaying(true)} />;
}

function EmbedThumb({ embed, src, tall, onPlay }: { embed: NonNullable<MessageView['embed']>; src: string | null; tall: boolean; onPlay: () => void }) {
  return (
    <div className={`embed-card ${embed.provider}`}>
      <button
        type="button"
        className={`embed-thumb ${tall ? 'tall' : 'wide'}`}
        disabled={embed.status !== 'ready' || !src}
        onClick={onPlay}
        aria-label={t('chat.playVideo', { provider: PROVIDER_NAME[embed.provider] })}
      >
        {embed.thumbUrl ? <img src={embed.thumbUrl} alt="" loading="lazy" /> : <span className={`embed-thumb-empty ${embed.provider}`} />}
        <span className="embed-play" aria-hidden="true">
          {embed.status === 'pending' ? <span className="spinner" /> : <Icon name="play" />}
        </span>
        <span className="embed-provider">{PROVIDER_NAME[embed.provider]}</span>
      </button>
      {(embed.title || embed.author) && (
        <div className="embed-meta">
          {embed.title && <span className="embed-title">{embed.title}</span>}
          {embed.author && <span className="embed-author">{embed.author}</span>}
        </div>
      )}
    </div>
  );
}
/** Messages from the same person within this window are grouped into one block. */
const GROUP_WINDOW_MS = 5 * 60_000;

interface Channel {
  id: string;
  kind: 'global' | 'tournament';
  tournamentId: string | null;
  title: string;
  unread: number;
  lastMessageAt: number | null;
  canPost: boolean;
  voiceEnabled: boolean;
  lastMessage: { authorName: string | null; mine: boolean; kind: string; deleted: boolean; preview: string } | null;
}

function previewText(c: Channel): string {
  const m = c.lastMessage;
  if (!m) return t('chat.noMessagesYet');
  const who = m.mine ? t('chat.you') : (m.authorName ?? t('app.name'));
  const what = m.deleted ? t('chat.deleted') : m.kind === 'voice' ? `🎤 ${t('chat.voiceMessage')}` : m.kind === 'image' ? `📷 ${t('chat.image')}` : m.preview;
  return `${who}: ${what}`;
}

export function useChannels() {
  return useQuery({ queryKey: ['channels'], queryFn: () => api.get<{ channels: Channel[] }>('/api/chat/channels') });
}

/** Sizes the chat to exactly the visible space below it, above the phone tab bar. */
function useFillViewport(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      const tabbar = document.querySelector<HTMLElement>('.tabbar');
      const bottom = tabbar && getComputedStyle(tabbar).display !== 'none' ? tabbar.offsetHeight + 10 : 24;
      const height = `${Math.max(220, window.innerHeight - top - bottom)}px`;
      if (el.style.height !== height) el.style.height = height;
    };
    update();
    // The main column changes size when something above the chat appears (e.g. the turn banner);
    // the page itself may not, so watch both.
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    const main = el.closest('.main');
    if (main) ro.observe(main);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref]);
}

export function ChatPage() {
  const { channelId } = useParams();
  const channels = useChannels();
  const selected = channelId ?? null;
  const current = channels.data?.channels.find((c) => c.id === selected);
  const box = useRef<HTMLDivElement>(null);
  useFillViewport(box);
  return (
    <div className="page">
      <h1 className="visually-hidden">{t('chat.title')}</h1>
      <div ref={box} className={`chat ${selected ? 'has-channel' : ''}`}>
        <nav className="chat-channels" aria-label={t('chat.channels')}>
          <div className="chat-head">{t('chat.channels')}</div>
          {channels.isPending && <Loading />}
          {channels.isError && <ErrorView error={channels.error} onRetry={() => channels.refetch()} />}
          <ul className="channel-list">
            {channels.data?.channels.map((c, i, list) => (
              <li key={c.id}>
                {c.kind === 'tournament' && list[i - 1]?.kind === 'global' && <div className="channel-group">{t('chat.tournamentChats')}</div>}
                <Link
                  className={`channel-item ${c.kind === 'global' ? 'global' : ''} ${c.unread > 0 ? 'has-unread' : ''}`}
                  to={`/keskustelu/${c.id}`}
                  aria-current={selected === c.id ? 'page' : undefined}
                >
                  <span className="channel-icon" aria-hidden="true">
                    {c.kind === 'global' ? <Icon name="users" /> : '♠'}
                  </span>
                  <span className="channel-text">
                    <span className="channel-line">
                      <span className="channel-title">{c.kind === 'global' ? t('chat.global') : c.title}</span>
                      {c.lastMessageAt && <span className="channel-time">{dayOrTime(c.lastMessageAt)}</span>}
                    </span>
                    <span className="channel-line">
                      <span className="channel-preview">{c.kind === 'global' && !c.lastMessage ? t('chat.globalHint') : previewText(c)}</span>
                      {c.unread > 0 && (
                        <span className="count" aria-label={t('chat.unread', { n: c.unread })}>
                          {c.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="chat-main">
          {selected ? (
            <>
              <div className="chat-head">
                <Link to="/keskustelu" className="btn btn-ghost btn-small chat-back" aria-label={t('app.back')}>
                  <Icon name="back" />
                </Link>
                <span className={`channel-icon ${current?.kind === 'global' ? 'global' : ''}`} aria-hidden="true">
                  {current?.kind === 'global' ? <Icon name="users" /> : '♠'}
                </span>
                {current ? (current.kind === 'global' ? t('chat.global') : current.title) : ''}
              </div>
              <ChannelView key={selected} channelId={selected} />
            </>
          ) : (
            <p className="empty">{t('chat.pickChannel')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers

function sameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

function dayLabel(ms: number): string {
  const now = Date.now();
  if (sameDay(ms, now)) return t('chat.today');
  if (sameDay(ms, now - 86_400_000)) return t('chat.yesterday');
  return new Intl.DateTimeFormat(localeTag, { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' }).format(ms);
}

function dayOrTime(ms: number): string {
  return sameDay(ms, Date.now()) ? timeOnly(ms) : new Intl.DateTimeFormat(localeTag, { day: 'numeric', month: 'numeric' }).format(ms);
}

/** A stable hue per person for their name in group chats (colour is never the only cue). */
function nameHue(id: string): number {
  return [...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 17);
}

// ---------------------------------------------------------------------------
// Channel view

export function ChannelView({ channelId, compact }: { channelId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const channels = useChannels();
  const channel = channels.data?.channels.find((c) => c.id === channelId);
  const latest = useQuery({
    queryKey: ['messages', channelId],
    queryFn: () => api.get<{ messages: MessageView[]; hasMore: boolean; lastReadSeq: number }>(`/api/chat/channels/${channelId}/messages?limit=40`),
  });
  const [older, setOlder] = useState<MessageView[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  const [firstUnread, setFirstUnread] = useState<number | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  useEffect(() => {
    setOlder([]);
    setOlderHasMore(null);
    setFirstUnread(null);
    setMenuFor(null);
    stickToBottom.current = true;
  }, [channelId]);

  const messages = useMemo(() => {
    const map = new Map<number, MessageView>();
    for (const m of [...older, ...(latest.data?.messages ?? [])]) map.set(m.seq, m);
    return [...map.values()].sort((a, b) => a.seq - b.seq);
  }, [older, latest.data]);
  const hasMore = olderHasMore ?? latest.data?.hasMore ?? false;

  // Screen readers hear new messages from others, one at a time (not the whole list on load).
  const [announce, setAnnounce] = useState('');
  const lastAnnounced = useRef<number | null>(null);
  useEffect(() => {
    const last = latest.data?.messages.at(-1);
    if (!last) return;
    if (lastAnnounced.current !== null && last.seq > lastAnnounced.current && !last.mine && !last.deleted) {
      setAnnounce(`${last.author?.displayName ?? ''}: ${last.body || (last.image ? t('chat.image') : last.voice ? t('chat.voiceMessage') : '')}`);
    }
    lastAnnounced.current = last.seq;
  }, [latest.data]);

  // Remember where unread messages started, then mark everything read.
  useEffect(() => {
    if (!latest.data) return;
    if (firstUnread === null) setFirstUnread(latest.data.lastReadSeq);
    const maxSeq = latest.data.messages.at(-1)?.seq ?? 0;
    if (maxSeq > latest.data.lastReadSeq) {
      void api.post(`/api/chat/channels/${channelId}/read`, { seq: maxSeq }).then(() => {
        // Otherwise reopening the channel would draw "new messages" above already-read ones.
        qc.setQueryData(['messages', channelId], (d: { lastReadSeq: number } | undefined) => (d ? { ...d, lastReadSeq: Math.max(d.lastReadSeq, maxSeq) } : d));
        void qc.invalidateQueries({ queryKey: ['channels'] });
        void qc.invalidateQueries({ queryKey: ['home'] });
      });
    }
  }, [latest.data, channelId, firstUnread, qc]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Close an open message menu on outside click or Escape.
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      if (e instanceof MouseEvent && (e.target as HTMLElement).closest('.msg-menu, .msg-menu-btn')) return;
      setMenuFor(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuFor]);

  const loadOlder = async () => {
    const first = messages[0];
    if (!first) return;
    const el = listRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    let res: { messages: MessageView[]; hasMore: boolean };
    try {
      res = await api.get(`/api/chat/channels/${channelId}/messages?limit=40&before=${first.seq}`);
    } catch (err) {
      toast(errorText(err), 'error');
      return;
    }
    stickToBottom.current = false;
    setOlder((o) => [...res.messages, ...o]);
    setOlderHasMore(res.hasMore);
    requestAnimationFrame(() => {
      if (el) el.scrollTop = el.scrollHeight - prevHeight;
    });
  };

  const refresh = () => void qc.invalidateQueries({ queryKey: ['messages', channelId] });
  const react = useMutation({
    mutationFn: ({ id, emoji }: { id: string; emoji: string }) => api.post(`/api/chat/messages/${id}/reactions`, { emoji }),
    onSettled: refresh,
    onError: (e) => toast(errorText(e), 'error'),
  });
  const [deleting, setDeleting] = useState<MessageView | null>(null);
  const [reason, setReason] = useState('');
  const del = useMutation({
    mutationFn: (m: MessageView) => api.del(`/api/chat/messages/${m.id}`, m.mine ? {} : { reason }),
    onSuccess: () => {
      setDeleting(null);
      setReason('');
    },
    onSettled: refresh,
    onError: (e) => toast(errorText(e), 'error'),
  });

  return (
    <div className={`channel-view ${compact ? 'compact' : ''}`}>
      <p className="visually-hidden" aria-live="polite">
        {announce}
      </p>
      <div
        className="messages"
        ref={listRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {latest.isPending && <Loading />}
        {latest.isError && <ErrorView error={latest.error} onRetry={() => latest.refetch()} />}
        {hasMore && (
          <button className="btn btn-small older-btn" onClick={loadOlder}>
            {t('chat.older')}
          </button>
        )}
        {latest.data && messages.length === 0 && <p className="chat-empty">{t('chat.empty')}</p>}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const newDay = !prev || !sameDay(prev.createdAt, m.createdAt);
          const first =
            newDay || !prev || prev.author?.id !== m.author?.id || m.createdAt - prev.createdAt > GROUP_WINDOW_MS || prev.kind === 'system';
          const showUnread = firstUnread !== null && m.seq === firstUnread + 1 && !m.mine && i > 0;
          const canDelete = !m.deleted && (m.mine || me?.role === 'superadmin');
          const hue = nameHue(m.author?.id ?? 'x');
          return (
            <div key={m.id}>
              {newDay && (
                <div className="day-sep">
                  <span>{dayLabel(m.createdAt)}</span>
                </div>
              )}
              {showUnread && <div className="unread-divider">{t('chat.newMessages')}</div>}
              <motion.div
                className={`bubble-row ${m.mine ? 'mine' : 'theirs'} ${first ? 'first' : ''}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                {!m.mine &&
                  (first ? <Avatar name={m.author?.displayName ?? '?'} url={m.author?.avatarUrl ?? null} size="s" /> : <span className="avatar-spacer" aria-hidden="true" />)}
                <article className={`bubble ${m.deleted ? 'deleted' : ''}`} aria-label={`${m.author?.displayName ?? ''}, ${dateTime(m.createdAt)}`}>
                  {!m.mine && first && (
                    <div className="bubble-author" style={{ ['--name-hue' as string]: String(hue) }}>
                      {m.author?.displayName ?? t('app.name')}
                    </div>
                  )}
                  {m.deleted ? (
                    <p className="bubble-text">
                      <span className="msg-deleted">{m.deleted.byModerator ? t('chat.deletedMod', { reason: m.deleted.reason ?? '' }) : t('chat.deleted')}</span>
                      <time className="bubble-time" dateTime={new Date(m.createdAt).toISOString()}>
                        {timeOnly(m.createdAt)}
                      </time>
                    </p>
                  ) : m.image ? (
                    <div className="bubble-media">
                      <ChatImage image={m.image} onOpen={() => setLightbox(m.image!.url)} />
                      {m.body ? (
                        <p className="bubble-text">
                          <Linkified text={m.body} />
                          <time className="bubble-time" dateTime={new Date(m.createdAt).toISOString()}>
                            {timeOnly(m.createdAt)}
                          </time>
                        </p>
                      ) : (
                        <time className="bubble-time on-media" dateTime={new Date(m.createdAt).toISOString()}>
                          {timeOnly(m.createdAt)}
                        </time>
                      )}
                    </div>
                  ) : m.voice ? (
                    <div className="bubble-voice">
                      <VoicePlayer url={m.voice.url} durationMs={m.voice.durationMs} />
                      <time className="bubble-time" dateTime={new Date(m.createdAt).toISOString()}>
                        {timeOnly(m.createdAt)}
                      </time>
                    </div>
                  ) : (
                    <>
                      {m.embed && <EmbedCard embed={m.embed} />}
                      <p className="bubble-text">
                        <Linkified text={m.body} />
                        <time className="bubble-time" dateTime={new Date(m.createdAt).toISOString()}>
                          {timeOnly(m.createdAt)}
                        </time>
                      </p>
                    </>
                  )}
                  {m.reactions.length > 0 && (
                    <div className="bubble-reactions">
                      {m.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          type="button"
                          className="reaction"
                          aria-pressed={r.mine}
                          onClick={() => react.mutate({ id: m.id, emoji: r.emoji })}
                          aria-label={t('chat.reactionCount', { emoji: r.emoji, n: r.count })}
                        >
                          {r.emoji}
                          {r.count > 1 && <span className="num">{r.count}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
                {!m.deleted && (
                  <div className="msg-menu-wrap">
                    <button
                      type="button"
                      className="msg-menu-btn"
                      aria-label={t('chat.messageActions')}
                      aria-expanded={menuFor === m.id}
                      onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}
                    >
                      <Icon name="more" />
                    </button>
                    <AnimatePresence>
                      {menuFor === m.id && (
                        <motion.div
                          className="msg-menu"
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setMenuFor(null);
                              e.stopPropagation();
                            }
                          }}
                          initial={{ opacity: 0, scale: 0.92, y: 4 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
                          transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                        >
                          <div className="msg-menu-emojis">
                            {REACTIONS.map((e) => (
                              <button
                                key={e}
                                type="button"
                                aria-label={t('chat.reactWith', { emoji: e })}
                                onClick={() => {
                                  react.mutate({ id: m.id, emoji: e });
                                  setMenuFor(null);
                                }}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                          {canDelete && (
                            <button
                              type="button"
                              className="msg-menu-item danger"
                              onClick={() => {
                                setMenuFor(null);
                                setDeleting(m);
                              }}
                            >
                              <Icon name="trash" />
                              {t('chat.delete')}
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </motion.div>
            </div>
          );
        })}
      </div>
      {channel && !channel.canPost ? (
        <p className="chat-empty">{t('chat.disabled')}</p>
      ) : (
        <Composer
          channelId={channelId}
          voiceEnabled={channel?.voiceEnabled ?? false}
          onSent={() => {
            stickToBottom.current = true;
            refresh();
          }}
        />
      )}
      <AnimatePresence>{lightbox && <ImageLightbox url={lightbox} onClose={() => setLightbox(null)} />}</AnimatePresence>
      {deleting && (
        <Dialog title={t('chat.deleteConfirm')} onClose={() => setDeleting(null)}>
          {!deleting.mine && (
            <div className="field">
              <label htmlFor="mod-reason">{t('chat.moderateReason')}</label>
              <input id="mod-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          <div className="btn-row">
            <button className="btn btn-danger" disabled={del.isPending || (!deleting.mine && reason.trim().length < 3)} onClick={() => del.mutate(deleting)}>
              {t('chat.delete')}
            </button>
            <button className="btn" data-close onClick={() => setDeleting(null)}>
              {t('app.cancel')}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice

function fmtClock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function VoicePlayer({ url, durationMs }: { url: string; durationMs: number | null }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(durationMs ?? 0);
  return (
    <div className="voice-player">
      <audio
        ref={audio}
        src={url}
        preload="metadata"
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime * 1000)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration * 1000);
        }}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <button type="button" className="voice-play" aria-label={playing ? t('chat.pause') : t('chat.play')} onClick={() => (playing ? audio.current?.pause() : void audio.current?.play())}>
        <Icon name={playing ? 'pause' : 'play'} />
      </button>
      <input
        type="range"
        className="voice-seek"
        min={0}
        max={Math.max(1, dur)}
        value={Math.min(pos, dur)}
        style={{ ['--p' as string]: `${dur ? (Math.min(pos, dur) / dur) * 100 : 0}%` }}
        aria-label={t('chat.voiceMessage')}
        aria-valuetext={`${fmtClock(pos)} / ${fmtClock(dur)}`}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (audio.current) audio.current.currentTime = v / 1000;
          setPos(v);
        }}
      />
      <span className="voice-time num">{fmtClock(playing || pos > 0 ? pos : dur)}</span>
    </div>
  );
}

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const m of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm']) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

type RecState =
  | { s: 'idle' }
  | { s: 'recording'; started: number }
  | { s: 'preview'; blob: Blob; url: string; durationMs: number }
  | { s: 'uploading'; blob: Blob; url: string; durationMs: number };

// ---------------------------------------------------------------------------
// Composer: text field that grows, with a microphone button that turns into send.

function Composer({ channelId, voiceEnabled, onSent }: { channelId: string; voiceEnabled: boolean; onSent: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const clientId = useRef(newKey());
  const area = useRef<HTMLTextAreaElement>(null);

  // Voice recording state lives here so the recorder can replace the text row.
  const [rec, setRec] = useState<RecState>({ s: 'idle' });
  const [recError, setRecError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const discardOnStop = useRef(false);
  const voiceClientId = useRef(newKey());
  const [attachment, setAttachment] = useState<{ file: File; url: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const attach = (file: File | undefined | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast(t('errors.invalid_image'), 'error');
    if (file.size > MAX_IMAGE_BYTES) return toast(t('chat.imageTooBig', { mb: MAX_IMAGE_BYTES / 1024 / 1024 }), 'error');
    setAttachment((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file) };
    });
    area.current?.focus();
  };
  const clearAttachment = () =>
    setAttachment((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });

  const uploadImage = async () => {
    if (!attachment || uploading) return;
    setUploading(true);
    const form = new FormData();
    form.append('caption', text);
    form.append('clientId', clientId.current);
    form.append('file', attachment.file, attachment.file.name || 'kuva');
    try {
      await api.upload(`/api/chat/channels/${channelId}/image`, form);
      clearAttachment();
      setText('');
      clientId.current = newKey();
      onSent();
    } catch (err) {
      toast(`${t('chat.sendFailed')} ${errorText(err)}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const send = useMutation({
    mutationFn: (body: string) => api.post(`/api/chat/channels/${channelId}/messages`, { body, clientId: clientId.current }),
    onSuccess: () => {
      setText('');
      clientId.current = newKey();
      onSent();
    },
    onError: (e) => toast(`${t('chat.sendFailed')} ${errorText(e)}`, 'error'),
  });

  // Auto-grow the text field up to a limit.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  useEffect(() => {
    if (rec.s !== 'recording') return;
    const id = setInterval(() => {
      const ms = Date.now() - rec.started;
      setElapsed(ms);
      if (ms >= MAX_VOICE_MS && recorder.current?.state === 'recording') recorder.current.stop();
    }, 200);
    return () => clearInterval(id);
  }, [rec]);

  // Release the microphone and any preview files when the composer goes away.
  const previewUrls = useRef<{ attachment?: string; voice?: string }>({});
  previewUrls.current = { attachment: attachment?.url, voice: rec.s === 'preview' || rec.s === 'uploading' ? rec.url : undefined };
  useEffect(
    () => () => {
      recorder.current?.stream.getTracks().forEach((tr) => tr.stop());
      for (const url of Object.values(previewUrls.current)) if (url) URL.revokeObjectURL(url);
    },
    [],
  );

  const submit = () => {
    if (attachment) return void uploadImage();
    if (text.trim() && !send.isPending) send.mutate(text);
  };
  const canSend = Boolean(text.trim() || attachment);

  const startRecording = async () => {
    setRecError(null);
    const mime = pickMime();
    if (!navigator.mediaDevices?.getUserMedia || !mime) return setRecError(t('chat.micMissing'));
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      return setRecError((err as DOMException).name === 'NotAllowedError' ? t('chat.micDenied') : t('chat.micMissing'));
    }
    chunks.current = [];
    discardOnStop.current = false;
    const r = new MediaRecorder(stream, { mimeType: mime });
    const started = Date.now();
    r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      if (discardOnStop.current) return setRec({ s: 'idle' });
      const blob = new Blob(chunks.current, { type: mime.split(';')[0] });
      setRec({ s: 'preview', blob, url: URL.createObjectURL(blob), durationMs: Math.min(Date.now() - started, MAX_VOICE_MS) });
    };
    recorder.current = r;
    r.start(250);
    setElapsed(0);
    setRec({ s: 'recording', started });
  };

  const discard = () => {
    if (rec.s === 'recording') {
      discardOnStop.current = true;
      recorder.current?.stop();
    }
    if (rec.s === 'preview') URL.revokeObjectURL(rec.url);
    setRec({ s: 'idle' });
    setRecError(null);
    voiceClientId.current = newKey();
  };

  const uploadVoice = async () => {
    if (rec.s !== 'preview') return;
    setRec({ ...rec, s: 'uploading' });
    const form = new FormData();
    form.append('durationMs', String(rec.durationMs));
    form.append('clientId', voiceClientId.current);
    form.append('file', rec.blob, `voice.${rec.blob.type.includes('mp4') ? 'm4a' : rec.blob.type.includes('ogg') ? 'ogg' : 'webm'}`);
    try {
      await api.upload(`/api/chat/channels/${channelId}/voice`, form);
      URL.revokeObjectURL(rec.url);
      setRec({ s: 'idle' });
      voiceClientId.current = newKey();
      onSent();
    } catch (err) {
      // Keep the recording so the user can retry.
      setRec({ ...rec, s: 'preview' });
      setRecError(`${t('chat.voiceFailed')} ${errorText(err)}`);
    }
  };

  return (
    <div
      className={`composer-wrap ${dragging ? 'dragging' : ''}`}
      onDragOver={(e) => {
        if (rec.s !== 'idle' || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (rec.s !== 'idle') return;
        e.preventDefault();
        setDragging(false);
        attach(e.dataTransfer.files[0]);
      }}
    >
      <AnimatePresence initial={false}>
        {attachment && rec.s === 'idle' && (
          <motion.div className="attach-preview" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <div className="attach-thumb">
              <img src={attachment.url} alt="" />
              <button type="button" className="attach-remove" aria-label={t('chat.removeImage')} onClick={clearAttachment} disabled={uploading}>
                <Icon name="close" />
              </button>
            </div>
            <span className="hint">{uploading ? t('chat.uploading') : t('chat.captionHint')}</span>
          </motion.div>
        )}
      </AnimatePresence>
      {recError && (
        <p className="composer-error" role="alert">
          {recError}
        </p>
      )}
      {rec.s === 'idle' ? (
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              attach(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <div className="composer-box">
            <button type="button" className="composer-attach" aria-label={t('chat.attachImage')} title={t('chat.attachImage')} onClick={() => fileInput.current?.click()}>
              <Icon name="image" />
            </button>
            <label htmlFor={`composer-${channelId}`} className="visually-hidden">
              {t('chat.placeholder')}
            </label>
            <textarea
              ref={area}
              id={`composer-${channelId}`}
              rows={1}
              maxLength={2000}
              placeholder={attachment ? t('chat.captionPlaceholder') : t('chat.placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={(e) => {
                const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
                if (file) {
                  e.preventDefault();
                  attach(file);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            {canSend || !voiceEnabled ? (
              <motion.button
                key="send"
                className="round-btn primary"
                disabled={!canSend || send.isPending || uploading}
                aria-label={t('chat.send')}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <Icon name="send" />
              </motion.button>
            ) : (
              <motion.button
                key="mic"
                type="button"
                className="round-btn primary"
                aria-label={t('chat.record')}
                onClick={startRecording}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <Icon name="mic" />
              </motion.button>
            )}
          </AnimatePresence>
        </form>
      ) : rec.s === 'recording' ? (
        <div className="composer recording" role="status">
          <button type="button" className="round-btn ghost" aria-label={t('chat.discard')} onClick={discard}>
            <Icon name="trash" />
          </button>
          <div className="composer-box rec-box">
            <span className="rec-dot" aria-hidden="true" />
            <span className="num">{t('chat.recording', { time: fmtClock(elapsed) })}</span>
            <span className="hint rec-limit">{t('chat.maxLength', { s: MAX_VOICE_MS / 1000 })}</span>
          </div>
          <button type="button" className="round-btn primary" aria-label={t('chat.stop')} onClick={() => recorder.current?.stop()}>
            <Icon name="stop" />
          </button>
        </div>
      ) : (
        <div className="composer">
          <button type="button" className="round-btn ghost" aria-label={t('chat.discard')} disabled={rec.s === 'uploading'} onClick={discard}>
            <Icon name="trash" />
          </button>
          <div className="composer-box preview-box" aria-label={t('chat.preview')}>
            <VoicePlayer url={rec.url} durationMs={rec.durationMs} />
          </div>
          <button type="button" className="round-btn primary" aria-label={t('chat.sendVoice')} disabled={rec.s === 'uploading'} onClick={uploadVoice}>
            <Icon name="send" />
          </button>
        </div>
      )}
    </div>
  );
}
